import React from 'react';
import { connect } from 'react-redux';
import { withLastLocation } from "react-router-last-location";

import { trackEvent } from '../../../helpers/analyticsHelper';
import { fetchSurveyData, saveSurveyData, callDisaggApi, checkIfDisaggFinished } from '../../../dataFetchers/surveyDataFetchers';
import { resetData } from '../../../actions/sharedActions';
import { nextSurveyQuestion, prevSurveyQuestion, clearSurveyData, updateSurveyCurrentQuestion } from '../../../actions/surveyActions';
import { getBECompatibleLocale, setPageTitle } from '../../../helpers/helper';

import { PENDING, SUCCESS, TIMEOUT, PAGE_EVENT_NAMES, SURVEY_QUESTION_TYPES } from '../../../constants/common';
import { DASHBOARD_HOME_PATH, DASHBOARD_SURVEY_PATH } from '../../../constants/paths';
import { SURVEY_QUESTION_STATE } from '../../../constants/common';

import SurveyPage from './SurveyPage';
import FooterMain from "../../standalone/common/Footer/FooterMain";

class SurveyPageMain extends React.Component {
  intervalFunc = null;
  startTime = null;
  endTime = null;
  state = {
    isDisaggLoading: false,
    lastLocation : DASHBOARD_HOME_PATH
  };

  componentDidMount() {
    setPageTitle("SURVEY_PAGE.TITLE", this.props.translations.data, this.props.configs.data);
    //call analytics function to save viewed event
    trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
      viewed: true
    }, this.props);
    this.setState(
      {lastLocation : this.props.lastLocation.pathname === DASHBOARD_SURVEY_PATH ? DASHBOARD_HOME_PATH : this.props.lastLocation.pathname}
    )
    this.props.fetchSurveyData(this.props.userIdentityDetails.userId, getBECompatibleLocale(this.props.configs.data));
  }

  componentDidUpdate(prevProps) {
    if(this.props.surveyData && this.props.surveyData.surveyQuestionsData.length && !prevProps.surveyData.currentQuestionObject) {
      let questionIndex = this.getQuestionIndexFromQueId(this.props.queryParams.questionId);
      let surveyAnswers = [];
      this.props.surveyData.surveyQuestionsData.filter((question, index) => {
        if (index < questionIndex) {
          surveyAnswers.push({
            questionId: question.id,
            value: question.answers
          });
        }
      });
      this.props.updateSurveyCurrentQuestion({
        currentQuestionObject: this.props.surveyData.surveyQuestionsData[questionIndex],
        currentQuestionIndex: questionIndex,
        currentAnswer: this.props.surveyData.surveyQuestionsData[questionIndex].answers,
        surveyAnswers: surveyAnswers
      });
    }
    //Second condition is for submitting last question
    if ((this.props.surveyData.currentQuestionIndex && this.props.surveyData.currentQuestionIndex > prevProps.surveyData.currentQuestionIndex) ||
      (!this.props.surveyData.currentQuestionIndex && this.props.surveyData.surveyFinished && prevProps.surveyData.currentQuestionIndex)) {
        let answers = this.props.surveyData.surveyAnswers;
        this.submitAnswer(answers, prevProps.surveyData.currentQuestionObject);
    }

    if (this.props.surveyData.surveySubmissionTimestamp && this.props.surveyData.surveySubmissionTimestamp !== prevProps.surveyData.surveySubmissionTimestamp) {
      this.startTime = new Date().getTime();
      this.intervalFunc = setInterval(() => this.checkIfDisaggFinished(), 2000);
    }

    if (this.props.surveyData.surveyDisaggStatus && this.props.surveyData.surveyDisaggStatus !== PENDING && this.props.surveyData.surveyDisaggStatus !== prevProps.surveyData.surveyDisaggStatus) {
      this.endTime = new Date().getTime();

      //Clear interval to stop polling api to check disagg status
      clearInterval(this.intervalFunc);

      //Save event for completion of disagg
      trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
        type: "hybridDisaggRunTime",
        startTime: this.startTime,
        endTime: this.endTime,
        duration: (this.endTime - this.startTime) / 1000,
        timedOut: this.props.surveyData.surveyDisaggStatus === TIMEOUT,
        success: this.props.surveyData.surveyDisaggStatus === SUCCESS
      })

      if (this.props.surveyData.surveyDisaggStatus === SUCCESS && this.props.configs.data["SURVEY.SHOW_DASHBOARD_LINK"] == "true") {
        this.props.resetData();
        // reset data is an async action and it may take sometime (few mins) to propogate to store, so redirect after a timeout
        setTimeout(() => {
          this.props.history.push(this.state.lastLocation)
        }, 100);
      }
    }
  }

  componentWillUnmount() {
    if (this.intervalFunc) {
      clearInterval(this.intervalFunc);
    }
    //If user has finished answering all questions, then delete all data so that on loading page next time, all apis with updated values are called
    this.props.clearSurveyData();
  }

  submitAnswer(answers, currentQuestionObject) {
    //Fetch latest answer from answers array and process data and call api
    let answer = answers[answers.length - 1];
    let originalAnswer = currentQuestionObject.originalAns;

    //check if answers are same
    //first condition is when both the anwers are not null
    //second condition is when both the answers are null
    if ((answer.value && originalAnswer && JSON.stringify(originalAnswer.sort()) === JSON.stringify(answer.value.sort())) || (!answer.value && !originalAnswer)) {
      return;
    }

    if (this.props.configs.data["enableSurveyDeselect"] === 'true') {
      //Check if any of the original answers are deselected
      let deselect = false;
      originalAnswer && originalAnswer.forEach(item => {
        if (!answer.value || !answer.value.includes(item)) {
          deselect = true;
        }
      })
      answer.deselect = deselect;  
    }
    
    //If answer is null then set it to empty array
    answer.value = answer.value || [];
    this.props.saveSurveyData([answer], this.props.userIdentityDetails.userId);
  }

  onClickNext() {
    let currentQuestionObject = this.props.surveyData.currentQuestionObject;
    //Saving next button click
    trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
      clicked: currentQuestionObject.type === SURVEY_QUESTION_TYPES.MULTIPLE ? "Save and Continue" : "Next",
      questionId: currentQuestionObject.id,
      questionType: currentQuestionObject.type,
      questionGroup: currentQuestionObject.group,
      questionStateOriginal: currentQuestionObject.state,
      timestamp: new Date().getTime(),
      answers: JSON.stringify(currentQuestionObject.answers),
      confirmed: currentQuestionObject.type === SURVEY_QUESTION_TYPES.SINGLE,
      questionStateNew: currentQuestionObject.answers ? (currentQuestionObject.state === 'ANSWERED' ? 'updated' : 'answered') : 'unanswered'
    });

    //After answering last question
    if (this.props.surveyData.currentQuestionIndex === this.props.surveyData.surveyQuestionsData.length) {
      this.setState({isDisaggLoading: true});
      let unansweredQuestions = this.props.surveyData.surveyAnswers.filter(item => item.value === null);
      if (unansweredQuestions.length > 0) {
        trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
          isSurveyViewed: true
        })
      }else {
        trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
          isSurveyCompleted: true
        })
      }
    }

    this.props.nextSurveyQuestion();
  }

  checkIfDisaggFinished() {
    this.props.checkIfDisaggFinished(this.props.userIdentityDetails.userId, this.props.surveyData.surveySubmissionTimestamp);
  }

  onClickPrev() {
    if (this.props.surveyData.surveyAnswers.length === 0) {
      return;
    }

    let currentQuestionObject = this.props.surveyData.currentQuestionObject;
    //Saving previous button click
    trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
      clicked: "Previous",
      questionId: currentQuestionObject.id,
      questionType: currentQuestionObject.type,
      questionGroup: currentQuestionObject.group,
      questionStateOriginal: currentQuestionObject.state,
      timestamp: new Date().getTime(),
      answers: JSON.stringify(currentQuestionObject.answers),
      confirmed: currentQuestionObject.type === SURVEY_QUESTION_TYPES.SINGLE
    });
    this.props.prevSurveyQuestion();
  }

  triggerDisagg() {
    this.setState({isDisaggLoading: true});

    //Track click on return to dashboard or click on close icon
    trackEvent(this.props.userIdentityDetails.userId, PAGE_EVENT_NAMES.SURVEY, {
      clicked: "Return to Insights"
    });

    //If user hasn't answered any quewstions, take him directly to dashboard instead of triggering disagg
    if (this.props.surveyData.currentQuestionIndex === 0 && !this.props.surveyData.surveyFinished && this.props.configs.data["SURVEY.SHOW_DASHBOARD_LINK"] == "true") {
      this.props.history.push(this.state.lastLocation);
    }else if (this.props.surveyData.currentQuestionObject) {
      //Save current answer if user triggers disagg before completing survey
      let answers = [...this.props.surveyData.surveyAnswers];
      answers.push({
        "questionId": this.props.surveyData.currentQuestionObject.id,
        "value": this.props.surveyData.currentAnswer
      });
      this.submitAnswer(answers, this.props.surveyData.currentQuestionObject);
    }
    this.props.callDisaggApi(this.props.userIdentityDetails.userId);
  }

  getQuestionIndexFromQueId(questionId) {
    let surveyQuestionsData = this.props.surveyData.surveyQuestionsData;
    let quesIndex = surveyQuestionsData.findIndex(question => question.id === questionId);
    if (surveyQuestionsData[quesIndex] && surveyQuestionsData[quesIndex].parent
        && surveyQuestionsData[quesIndex].parent.id && this.checkIfParentQuestionIsUnanswered(surveyQuestionsData[quesIndex].parent.id)) {
        quesIndex = this.getQuestionIndexFromQueId(surveyQuestionsData[quesIndex].parent.id);
    }
    return quesIndex > 0 ? quesIndex : 0;
  }

  checkIfParentQuestionIsUnanswered(parentId) {
    let parentQuestion = this.props.surveyData.surveyQuestionsData.filter(question => question.id === parentId)[0];
    return parentQuestion.state === SURVEY_QUESTION_STATE.UNANSWERED;
  }

  render(){
    return(
      <>
      <div className="survey-page-main">
         <SurveyPage surveyData={this.props.surveyData} onClickNext={() => this.onClickNext()} onClickPrev={() => this.onClickPrev()} triggerDisagg={() => this.triggerDisagg()} translations={this.props.translations.data} configs={this.props.configs} isDisaggLoading={this.state.isDisaggLoading}/>
      </div>
        {this.props.configs && this.props.configs.data && this.props.configs.data["DASHBOARD.SURVEY_PAGE.FOOTER.SHOW"] === "true" ?
          (<FooterMain configs={this.props.configs} translations={this.props.translations} footerNote={this.props.translations.data["FOOTER.HOME_SURVEY_PAGE.FOOTER_NOTE"]}/>)
        : ''
        }
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
      surveyData : state.surveyData,
      userIdentityDetails: state.userIdentityDetails,
      translations: state.translationsData,
      configs: state.configs,
      queryParams: state.queryParams
  }
}

const mapDispatchToProps = dispatch => {
  return {
    fetchSurveyData: (userId, locale) => dispatch(fetchSurveyData(userId, locale)),
    nextSurveyQuestion: () => dispatch(nextSurveyQuestion()),
    prevSurveyQuestion: () => dispatch(prevSurveyQuestion()),
    saveSurveyData: (payload, userId) => dispatch(saveSurveyData(payload, userId)),
    callDisaggApi: (userId) => dispatch(callDisaggApi(userId)),
    checkIfDisaggFinished: (userId, timestamp) => dispatch(checkIfDisaggFinished(userId, timestamp)),
    clearSurveyData: () => dispatch(clearSurveyData()),
    resetData: () => dispatch(resetData()),
    updateSurveyCurrentQuestion: (payload) => dispatch(updateSurveyCurrentQuestion(payload))
  }
}

export default withLastLocation(connect(mapStateToProps, mapDispatchToProps)(SurveyPageMain));
